import React, { useEffect, useState } from 'react';
import { Button, MenuItem, Spinner, TextField, Typography } from 'seagull';
import { StepTemplate } from "../StepTemplate";
import { Step } from "../VendorOnboardingModal.utils";
import styles from "./QBOSetupStep.module.scss";
import { useQboInVob } from "../useQboInVob";
import sortBy from 'lodash/sortBy';
import capitalize from 'lodash/capitalize';
import { useStoreActions } from "../../../store/store-context";
import { Checkmark } from 'seagull/icons';
import { useOnMount } from "../../../hooks/useOnMount";
import { VobQboState } from "../../../types/api.model";
export function QBOSetupStep(props) {
  var _integration$accounts, _integration$accounts2, _integration$accounts3;
  const {
    onContinue
  } = props;
  const {
    getQboAccounts,
    waitForReadyIntegration,
    setPendingConnect
  } = useQboInVob();
  const [qboAccounts, setQboAccounts] = useState();
  const [integration, setIntegration] = useState();
  useOnMount(() => {
    setPendingConnect(VobQboState.Done);
  });
  useEffect(() => {
    waitForReadyIntegration().then(async integration => {
      const accounts = await getQboAccounts();
      const sortedAccounts = {
        bank: sortBy(accounts.bank, account => account.name),
        expense: sortBy(accounts.expense, account => account.name),
        clearing: sortBy(accounts.clearing, account => account.name)
      };
      setQboAccounts(sortedAccounts);
      setIntegration(integration);
    });
  }, []);
  return /*#__PURE__*/React.createElement(StepTemplate, {
    currentStep: Step.QBOSetup,
    stepper: "Step 3 of 3",
    header: "Connect to QuickBooks Online",
    subHeader: "Choose the accounts you'd like to use. Don't worry, you can change it anytime.",
    footer: /*#__PURE__*/React.createElement("div", {
      className: styles.bottom
    }, /*#__PURE__*/React.createElement(Button, {
      "data-testid": "vob-step-".concat(Step.QBOSetup, "-continue"),
      onClick: () => onContinue(true)
    }, "Continue to Anchor"))
  }, integration ? /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement(AccountRow, {
    linkType: LinkType.Bank,
    initialValue: integration === null || integration === void 0 || (_integration$accounts = integration.accounts) === null || _integration$accounts === void 0 || (_integration$accounts = _integration$accounts.bank) === null || _integration$accounts === void 0 || (_integration$accounts = _integration$accounts.synced) === null || _integration$accounts === void 0 ? void 0 : _integration$accounts.integratedId,
    qboAccounts: (qboAccounts === null || qboAccounts === void 0 ? void 0 : qboAccounts.bank) || undefined,
    placeholder: "Choose a bank account for deposited funds"
  }), /*#__PURE__*/React.createElement(AccountRow, {
    linkType: LinkType.Expense,
    initialValue: integration === null || integration === void 0 || (_integration$accounts2 = integration.accounts) === null || _integration$accounts2 === void 0 || (_integration$accounts2 = _integration$accounts2.expense) === null || _integration$accounts2 === void 0 || (_integration$accounts2 = _integration$accounts2.synced) === null || _integration$accounts2 === void 0 ? void 0 : _integration$accounts2.integratedId,
    qboAccounts: (qboAccounts === null || qboAccounts === void 0 ? void 0 : qboAccounts.expense) || undefined,
    placeholder: "Choose an expense account for merchant fees"
  }), /*#__PURE__*/React.createElement(AccountRow, {
    linkType: LinkType.Clearing,
    initialValue: integration === null || integration === void 0 || (_integration$accounts3 = integration.accounts) === null || _integration$accounts3 === void 0 || (_integration$accounts3 = _integration$accounts3.clearing) === null || _integration$accounts3 === void 0 || (_integration$accounts3 = _integration$accounts3.synced) === null || _integration$accounts3 === void 0 ? void 0 : _integration$accounts3.integratedId,
    qboAccounts: (qboAccounts === null || qboAccounts === void 0 ? void 0 : qboAccounts.clearing) || undefined,
    placeholder: "Choose a clearing account for collected funds"
  })) : /*#__PURE__*/React.createElement("div", {
    "data-testid": "pending-accounts-spinner",
    className: styles.spinnerContainer
  }, /*#__PURE__*/React.createElement(Spinner, null), /*#__PURE__*/React.createElement(Typography, null, "Just a moment, bringing QuickBooks data to you...")));
}
var LinkType = /*#__PURE__*/function (LinkType) {
  LinkType["Bank"] = "bank";
  LinkType["Expense"] = "expense";
  LinkType["Clearing"] = "clearing";
  return LinkType;
}(LinkType || {});
const AccountRow = _ref => {
  let {
    linkType,
    initialValue,
    qboAccounts,
    placeholder
  } = _ref;
  const {
    api
  } = useStoreActions();
  const [integratedId, setIntegratedId] = useState(initialValue);
  const [mapStatus, setMapStatus] = useState(MapStatus.NotSaved);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.accountRow,
    "data-testid": "".concat(linkType, "-account-wrapper")
  }, /*#__PURE__*/React.createElement(TextField, {
    "data-testid": "".concat(linkType, "-account"),
    label: integratedId && "".concat(capitalize(linkType), " Account"),
    placeholder: placeholder,
    value: integratedId,
    onChange: e => {
      setMapStatus(MapStatus.Saving);
      const newIntegratedId = e.target.value;
      setIntegratedId(newIntegratedId);
      api.qbo.linkAccount({
        integratedId: newIntegratedId,
        linkType
      }).then(() => setMapStatus(MapStatus.Saved)).catch(() => setMapStatus(MapStatus.NotSaved));
    },
    select: true,
    minWidth: "412px"
  }, (qboAccounts || []).map(account => /*#__PURE__*/React.createElement(MenuItem, {
    key: account.id,
    value: account.id,
    "data-testid": account.id
  }, account.name))), /*#__PURE__*/React.createElement(SaveStatus, {
    mapStatus: mapStatus
  }));
};
var MapStatus = /*#__PURE__*/function (MapStatus) {
  MapStatus["Saved"] = "saved";
  MapStatus["NotSaved"] = "notSaved";
  MapStatus["Failed"] = "failedToSave";
  MapStatus["Saving"] = "saving";
  return MapStatus;
}(MapStatus || {});
export function SaveStatus(props) {
  const {
    mapStatus
  } = props;
  let element;
  switch (mapStatus) {
    case MapStatus.Saved:
      element = /*#__PURE__*/React.createElement("div", {
        className: styles.mapped,
        "data-testid": "saved"
      }, /*#__PURE__*/React.createElement(Checkmark, null));
      break;
    case MapStatus.NotSaved:
      element = null;
      break;
    case MapStatus.Failed:
      element = null;
      break;
    case MapStatus.Saving:
      element = /*#__PURE__*/React.createElement(Spinner, {
        "data-testid": "save-spinner",
        size: 32
      });
      break;
    default:
      element = null;
  }
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "save-status",
    className: styles.saveStatus
  }, element);
}